import {
  createErr,
  createOk,
  isErr,
  okOrElseForMaybe,
  Result,
  unwrapOk,
} from "./deps/option-t.ts";

export interface InvalidURLError {
  name: "InvalidURLError";
  message: string;
}
export interface NotFoundError {
  name: "NotFoundError";
  message: string;
}
/**
 * Extracts the source map URL from the given code.
 *
 * This code is based on https://github.com/evanw/source-map-visualization/blob/f3e9dfec20e7bfd9625d03dd0d427affa74a9c43/code.js#L103-L145 under @evanw 's license.
 *
 * @param code - The code from which to extract the source map URL.
 * @param _lang - The language of the code (currently only supports `"js"`).
 * @returns The extracted source map URL as a `URL` object, or `null` if no source map URL is found.
 */
export const extractSourceMapURL = (
  code: string,
  base: string | URL | undefined,
): Result<
  { url: URL; start: number; end: number },
  InvalidURLError | NotFoundError
> => {
  const result = okOrElseForMaybe(
    extract(code),
    () => ({
      name: "NotFoundError",
      message: "Source map URL is not found",
    } as NotFoundError),
  );
  if (isErr(result)) return result;
  const { url, start, end } = unwrapOk(result);
  return URL.canParse(url, base)
    ? createOk({ url: new URL(url, base), start, end })
    : createErr({
      name: "InvalidURLError",
      message: `Invalid URL: ${url}`,
    });
};

const extract = (code: string) => {
  // Check for both "//" and "/*" comments. This is mostly done manually
  // instead of doing it all with a regular expression because Firefox's
  // regular expression engine crashes with an internal error when the
  // match is too big.
  for (const match of code.matchAll(/\/([*/])[#@] *sourceMappingURL=/g)) {
    const start = match.index + match[0].length;
    const n = code.length;
    let end = start;
    while (end < n && code.charCodeAt(end) > 32) {
      end++;
    }
    if (end === start) continue;
    if (match[1] === "/" || code.indexOf("*/", end) > 0) {
      return {
        url: code.slice(start, end),
        start,
        end,
      };
    }
  }
};

// denoCacheMetadata={"headers":{"vary":"Authorization,Accept-Encoding,Origin","via":"1.1 varnish","date":"Fri, 08 Nov 2024 10:10:00 GMT","x-content-type-options":"nosniff","x-served-by":"cache-iad-kcgs7200100-IAD","cross-origin-resource-policy":"cross-origin","etag":"W/\"84346b547c3dba6ab627814d1aba83e79e25c2a959f4e5fd67ea27256f246654\"","x-xss-protection":"1; mode=block","x-github-request-id":"7001:179572:12C5ACF:147FFB4:672DDE07","source-age":"90","accept-ranges":"bytes","cache-control":"max-age=300","x-cache":"HIT","x-cache-hits":"0","content-type":"text/plain; charset=utf-8","x-timer":"S1731060601.757859,VS0,VE1","expires":"Fri, 08 Nov 2024 10:15:00 GMT","x-frame-options":"deny","content-security-policy":"default-src 'none'; style-src 'unsafe-inline'; sandbox","access-control-allow-origin":"*","x-fastly-request-id":"fef38ce87621628ec0f6796d1a51965da2e77371","strict-transport-security":"max-age=31536000"},"url":"https://raw.githubusercontent.com/takker99/ScrapJupyter/v1.7.4/extractSourceMapURL.ts","time":1731060600}